import { ref, onMounted, nextTick } from 'vue';
import { useThrottleFn, createSharedComposable } from '@vueuse/core';

export const useScrollToBottom = createSharedComposable((containerSelector: string) => {
    // 添加自动滚动控制状态
    const autoScrollEnabled = ref(true);
    let userHasScrolled = false;
    let lastContainerWidth = 0; // 记录容器宽度，用于检测布局变化

    /**
     * 获取实际的滚动容器
     */
    function getScrollContainer(): Element | null {
        const element = document.querySelector(containerSelector);
        if (!element) return null;

        // 直接返回元素本身
        return element;
    }

    async function scrollToBottom(force = false) {
        await nextTick()
        await (new Promise(resolve => setTimeout(resolve, 110)))
        const container = getScrollContainer();

        if (!container) return;

        // 检查是否在底部附近（允许100px误差）
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;

        // 只有在底部附近或强制执行时才滚动
        if (force || isAtBottom) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        }
    }

    const scrollToBottomThrottle = useThrottleFn(scrollToBottom, 500, true, false);

    // 添加监听用户滚动的方法
    function setupScrollListener() {
        const container = getScrollContainer();
        if (!container) return;

        // 记录初始宽度
        lastContainerWidth = container.clientWidth;

        container.addEventListener('scroll', () => {
            // 检查容器宽度是否发生变化（布局变化导致的滚动）
            const currentWidth = container.clientWidth;
            if (currentWidth !== lastContainerWidth) {
                // 宽度变化了，这是布局变化导致的，不是用户滚动
                lastContainerWidth = currentWidth;
                // 如果处于自动滚动状态，重新滚动到底部
                if (autoScrollEnabled.value) {
                    setTimeout(() => scrollToBottom(true), 100);
                }
                return; // 不处理为用户滚动
            }

            if (!userHasScrolled) {
                userHasScrolled = true;
            }

            // 检查是否滚动到底部（允许误差）
            const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

            // 如果用户滚动到底部，重新启用自动滚动
            if (isAtBottom) {
                autoScrollEnabled.value = true;
            } else {
                autoScrollEnabled.value = false;
            }
        });
    }

    // 在组件挂载后调用此方法进行设置
    function initScrollBehavior() {
        nextTick(() => {
            setupScrollListener();
        });
    }

    // 重置自动滚动状态的方法
    function resetAutoScroll(forceEnable = true) {
        autoScrollEnabled.value = forceEnable;
        userHasScrolled = false;
        if (forceEnable) {
            scrollToBottom();
        }
    }

    onMounted(() => {
        initScrollBehavior();
    })

    return {
        scrollToBottom,
        scrollToBottomThrottle,
        resetAutoScroll,
    }
})
